/**
 * Invite a farmer — referral-attribution share screen (web port).
 *
 * Web port of apps/mobile/app/invite.tsx. Lets an existing farmer hand a friend
 * a link that pre-attributes the referral: when the invitee opens the link and
 * signs up, the caller is credited as referrer.
 *
 * The referrer id is THIS user's id, read from the persisted access token's
 * `sub` claim (mirrors _app.my-contributions.tsx) — no network call. This screen
 * calls NO backend endpoint; it only builds + shares the link.
 *
 * Link format: the mobile screen builds the deep link `fse://signup?ref=<sub>`
 * (signup.tsx reads the `ref` query param + passes it to POST /auth/signup as
 * `referrerId`, stamping users.referred_by_user_id — migration 0017). The web
 * equivalent points at the public signup route on this origin:
 *   `${window.location.origin}/signup?ref=<sub>`
 * mirroring the same `?ref=<userId>` attribution mechanism over a web URL.
 *
 * GAP: as of this slice the WEB signup route (signup.tsx) does NOT read a `ref`
 * query param — it never sends `referrerId` to /auth/signup. So a link opened in
 * the web app attributes nothing yet; the web signup wizard must be taught to
 * read `ref` (like the mobile one) for web-side attribution to take effect. The
 * link shape is forward-compatible: it already matches the mobile `?ref=` param.
 *
 * Copy goes through i18next (TD-088) under invite.* — the i18n agent scans this
 * file for the key set.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Text,
  TextField,
} from '@fse/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { readAccessTokenClaims } from '../api/client';

/** Build the attributed web signup link for a given referrer user id. Mirrors
 *  the mobile `fse://signup?ref=<userId>` deep link, but as a web URL on this
 *  origin so it opens the public /signup route in a browser. */
function buildInviteLink(origin: string, userId: string): string {
  return `${origin}/signup?ref=${encodeURIComponent(userId)}`;
}

export function InviteScreen() {
  const { t } = useTranslation();
  const sub = readAccessTokenClaims()?.sub;

  // Transient "Copied!" confirmation — local state, reset after ~2s. No toast lib.
  const [copied, setCopied] = useState(false);

  // Could not derive the caller's identity from the token — surface as an error
  // rather than render a broken link (mirrors _app.my-contributions.tsx).
  if (!sub) {
    return (
      <Stack spacing={3}>
        <Box>
          <Text variant="headline-small" as="h2">
            {t('invite.title')}
          </Text>
          <Box sx={{ mt: 0.5 }}>
            <Text variant="body-medium" color="text.secondary">
              {t('invite.subtitle')}
            </Text>
          </Box>
        </Box>
        <Alert severity="error">{t('invite.noIdentity')}</Alert>
      </Stack>
    );
  }

  const origin =
    typeof window !== 'undefined' ? window.location.origin : '';
  const inviteLink = buildInviteLink(origin, sub);

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable / denied — leave the read-only field so the user
      // can select + copy manually; no destructive error needed.
    }
  }

  async function onShare() {
    // Guarded by the caller (button only renders when navigator.share exists).
    try {
      await navigator.share({
        title: t('invite.shareTitle'),
        text: t('invite.shareText'),
        url: inviteLink,
      });
    } catch (err) {
      // User dismissing the share sheet rejects with AbortError — ignore it;
      // any other failure is non-fatal (the link is still shown inline).
      if (err instanceof DOMException && err.name === 'AbortError') return;
    }
  }

  const canShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  return (
    <Stack spacing={3}>
      <Box>
        <Text variant="headline-small" as="h2">
          {t('invite.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t('invite.subtitle')}
          </Text>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Text variant="body-medium" color="text.primary">
              {t('invite.body')}
            </Text>

            <TextField
              label={t('invite.linkLabel')}
              value={inviteLink}
              fullWidth
              slotProps={{
                input: { readOnly: true },
                htmlInput: {
                  'aria-label': t('invite.linkLabel'),
                  style: { fontFamily: 'monospace', fontSize: 14 },
                },
              }}
              onFocus={(e) => e.currentTarget.select()}
            />

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button onClick={onCopy}>
                {copied ? t('invite.copied') : t('invite.copy')}
              </Button>
              {canShare && (
                <Button variant="outlined" onClick={onShare}>
                  {t('invite.share')}
                </Button>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
